import axios, { type Axios, type AxiosError, type AxiosResponse } from 'axios';
import { isNumber as _isNumber, update as _update } from 'lodash';

import authService, { authTokenService } from '../../authentication/auth.service';
import deviceService from '../../authentication/device.service';
import { notificationResponseError } from '../../interceptors/notifier.interceptor';
import notificationService from '../../notifications/notificationService';
import { loginStorage } from '../../pre-login/helpers/loginStorage';
import { SineHeader } from '../../shared/types';
import rxEventHub, { RxEvent } from '../events/rx';
import socketEventHub from '../events/sockets';

const MAX_AUTH_ATTEMPTS = 5;

let inProgress = 0;

const interceptorService: {
  request: Parameters<Axios['interceptors']['request']['use']>[0];
  response: Parameters<Axios['interceptors']['response']['use']>[0];
  responseError: (error: AxiosError) => Promise<AxiosResponse | void>;
} = {
  async request(config) {
    inProgress++;
    socketEventHub.setInProgress(inProgress > 0);

    await authService.checkForRefresh(config);

    config.withCredentials = true;

    const { authToken, adminAccessToken } = authService.getCurrentTokens();

    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    if (adminAccessToken) {
      config.headers['x-sine-admin-access'] = adminAccessToken;
    }

    return config;
  },

  response(response) {
    inProgress--;
    socketEventHub.setInProgress(inProgress > 0);
    const headers = response.headers;

    socketEventHub.setRquid(headers[SineHeader.X_SINE_RQUID]);
    return response;
  },

  async responseError(error) {
    const response = error.response;

    inProgress--;
    socketEventHub.setInProgress(inProgress > 0);

    if (!response || Object.keys(response).length === 0) {
      notificationResponseError(error);
      return Promise.reject();
    }

    socketEventHub.setRquid(response.headers[SineHeader.X_SINE_RQUID]);
    notificationResponseError(error);

    const isSineCoreRequest = response.config.url?.includes(window.env.REACT_APP_SINE_CORE_API_URI);
    const status = response.status;
    const errorCode = (response.data as AxiosError).code;

    if (status === 401 && isSineCoreRequest) {
      if (errorCode === 'REFRESH_TOKEN_EXPIRED') {
        rxEventHub.broadcast(RxEvent.LOGOUT, {});
        const msg = 'Your session has timed out';
        window.location.href = `/login?transitionMsg=${msg}`;
      }

      authTokenService.clear();

      if (errorCode === 'DEVICEID_ERROR') {
        deviceService.trashDeviceToken();
      }

      if (errorCode === 'TOKEN_INVALID_ERROR') {
        notificationService.addNotification({
          message: 'Please login and try again',
          variant: 'warn',
        });
        const email = (response.data as any).email;
        if (email) {
          loginStorage.setEmail(email);
        }
        window.location.href = `${window.env.PUBLIC_URL}login?redirect=${encodeURIComponent(window.location.href)}`;
      }

      return Promise.reject(response);
    }

    if (status === 498) {
      if (errorCode === 'TOKEN_EXPIRED_ERROR') {
        await authService.refresh();

        _update(response, 'config.attempts', (v) => {
          return _isNumber(v) ? v + 1 : 0;
        });

        const exceededMaxAttempts = (response.config as any).attempts > MAX_AUTH_ATTEMPTS;
        let retriedResponse;
        try {
          if (exceededMaxAttempts) {
            throw new Error('Too many auth attempts');
          } else {
            retriedResponse = await axios(response.config);
          }
        } catch (err) {
          return Promise.reject(response);
        }
        return Promise.resolve(retriedResponse);
      } else if (errorCode === 'SYSADMIN_TOKEN_EXPIRED_ERROR' || errorCode === 'SYSADMIN_TOKEN_INVALID_ERROR') {
        notificationService.addNotification({
          message: '!!! Your admin access session has expired, you will be returned to your own account',
          variant: 'danger',
        });
        setTimeout(() => {
          authService.handleSysAdminTokenExpired();
          window.location.reload();
        }, 2000);
      }
    }

    if (status === 403 && errorCode === 'CHALLENGE_REQUIRED_ERROR') {
      return Promise.reject(response.data);
    }

    return Promise.reject(response);
  },
};

export default interceptorService;
